import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, safeLocalSignOut } from '@/lib/supabase';
import { getAbsoluteCallbackUrl } from '@/lib/auth-redirect';
import { isAuthorizedAdmin } from '@/lib/admin-access';
import EngineStatusIndicator from '@/components/feature/EngineStatusIndicator';

export default function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('admin@chin.vn');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasSession(!!session);
    }).catch(() => {
      safeLocalSignOut();
    });
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSendCode = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!isAuthorizedAdmin(email)) {
      setError('Access denied. This email is not authorized to sign in.');
      return;
    }

    setLoading(true);

    try {
      const { error: sendError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: false,
          emailRedirectTo: getAbsoluteCallbackUrl(),
        },
      });

      if (sendError) {
        const msg = sendError.message.toLowerCase();
        if (msg.includes('not found') || msg.includes('no account') || msg.includes('user not found')) {
          setError('No account found for this email');
        } else {
          setError(sendError.message);
        }
        setLoading(false);
        return;
      }

      setSuccess('Login code sent to your email');
      setCode('');
      setStep('code');
      setResendCooldown(30);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
    }

    setLoading(false);
  };

  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (code.length < 6 || code.length > 8) {
      setError('Please enter the code (6-8 characters)');
      return;
    }

    if (!isAuthorizedAdmin(email)) {
      setError('Access denied. This email is not authorized to sign in.');
      return;
    }

    setLoading(true);

    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: code,
        type: 'email',
      });

      if (verifyError) {
        setError(verifyError.message);
        setLoading(false);
        return;
      }

      if (data.session) {
        const pendingCode = localStorage.getItem('ctrader_pending_oauth_code');
        if (pendingCode) {
          navigate('/dashboard?code=' + encodeURIComponent(pendingCode));
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { error: sendError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: false,
          emailRedirectTo: getAbsoluteCallbackUrl(),
        },
      });

      if (sendError) {
        setError(sendError.message);
      } else {
        setSuccess('A new code has been sent');
        setResendCooldown(30);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
    }

    setLoading(false);
  };

  const handleChangeEmail = () => {
    setStep('email');
    setCode('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center mx-auto mb-4">
            <i className="ri-flashlight-line text-background-50 text-xl"></i>
          </div>
          <h1 className="text-2xl font-brand font-bold text-foreground-950">Chín Hồng Mao</h1>
          <p className="text-foreground-600 text-sm mt-1">Automated Trading Platform</p>
        </div>

        <EngineStatusIndicator />

        {hasSession && (
          <div className="mb-5 bg-primary-500/10 border border-primary-500/20 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse shrink-0"></div>
              <span className="text-primary-400 text-xs font-medium whitespace-nowrap">Session active — bots running in background</span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        <div className="bg-background-100 rounded-xl border border-background-200/70 p-6">
          {step === 'email' ? (
            <>
              <h2 className="text-sm font-heading font-semibold text-foreground-900 mb-1">Sign In</h2>
              <p className="text-foreground-600 text-xs mb-5">
                Enter your email and we'll send you a one-time login code.
              </p>

              <form onSubmit={handleSendCode}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-red-400 text-xs">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-background-50 border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </span>
                    ) : (
                      'Send Login Code'
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-sm font-heading font-semibold text-foreground-900 mb-1">Enter Code</h2>
              <p className="text-foreground-600 text-xs mb-5">
                We sent a code to <span className="text-foreground-900 font-medium">{email}</span>
              </p>

              <form onSubmit={handleVerifyCode}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">Verification Code</label>
                    <input
                      type="text"
                      name="otp_code"
                      value={code}
                      onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 8)); setError(''); }}
                      placeholder="••••••••"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      className="w-full px-3 py-3 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-2xl text-center tracking-[0.5em] font-bold placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-red-400 text-xs">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="bg-primary-500/10 border border-primary-500/20 rounded-lg px-3 py-2 text-primary-400 text-xs">
                      {success}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || code.length < 6 || code.length > 8}
                    className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-background-50 border-t-transparent rounded-full animate-spin"></div>
                        Verifying...
                      </span>
                    ) : (
                      'Sign In'
                    )}
                  </button>

                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resendCooldown > 0 || loading}
                      className="text-primary-400 hover:text-primary-300 font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </button>
                    <button
                      type="button"
                      onClick={handleChangeEmail}
                      className="text-foreground-500 hover:text-foreground-800 font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Use different email
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-foreground-500 text-xs mt-4">
          Connect your cTrader account after logging in
        </p>
      </div>
    </div>
  );
}